import { useCallback, useEffect, useMemo, useState } from 'react'
import { z } from 'zod'
import { logError } from 'lib/tracking/datadog'

declare global {
  interface Window {
    grecaptcha?: {
      reset?: () => void
      execute: (siteKey: string, options: { action: string }) => Promise<string>
      ready: (callback: () => void) => void
    }
  }
}

const RECAPTCHA_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_KEY
const RECAPTCHA_HEADER = 'x-grecaptcha'
const RECAPTCHA_ACTIONS = {
  LOGIN: 'LOGIN',
} as const
const RECAPTCHA_CONFIG = {
  MAX_RETRIES: 2,
  ERROR_TOKEN: 'error-token',
  SCRIPT_ID: 'recaptcha-script',
  BADGE_SELECTOR: '.grecaptcha-badge',
  INITIALIZATION_TIMEOUT: 5000,
  SCRIPT_RETRY_MAX: 2,
  SCRIPT_RETRY_DELAYS: [1000, 2000],
} as const

const reCaptchaSchema = z.object({
  reset: z.function().optional(),
  ready: z.function().args(z.function()),
  execute: z
    .function()
    .args(z.string(), z.object({ action: z.string() }))
    .returns(z.promise(z.string())),
})

type RecaptchaActions = keyof typeof RECAPTCHA_ACTIONS

interface ExecuteRecaptchaParams {
  reCaptchaKey: string
  action: RecaptchaActions
}

export const useReCaptcha = () => {
  const [scriptLoaded, setScriptLoaded] = useState(false)
  const [clientReady, setClientReady] = useState(false)
  const [loadingError, setLoadingError] = useState<string | null>(null)

  useEffect(() => {
    if (!RECAPTCHA_KEY) {
      setLoadingError('reCAPTCHA key is not configured')
      return
    }

    const loadScript = (attempt: number) => {
      setScriptLoaded(false)
      setClientReady(false)
      setLoadingError(null)

      // Remove existing script if any
      const existingScript = document.getElementById(RECAPTCHA_CONFIG.SCRIPT_ID)

      if (existingScript) {
        existingScript.remove()
      }

      const script = document.createElement('script')

      script.id = RECAPTCHA_CONFIG.SCRIPT_ID
      script.src = `https://www.recaptcha.net/recaptcha/api.js?render=${RECAPTCHA_KEY}`
      script.async = true
      script.defer = true

      script.onload = () => {
        let initializationTimeout: NodeJS.Timeout

        setScriptLoaded(true)
        setLoadingError(null)

        // Check if client is ready after script loads
        if (window.grecaptcha && 'ready' in window.grecaptcha) {
          let isClientReady = false

          window.grecaptcha.ready(() => {
            isClientReady = true
            setClientReady(true)
            clearTimeout(initializationTimeout)
          })

          // Add timeout for client initialization
          initializationTimeout = setTimeout(() => {
            if (!isClientReady) {
              const errorMsg = `reCAPTCHA client initialization timeout (attempt ${attempt})`

              logError(new Error(errorMsg), { attempt, name: 'useReCaptcha_init_timeout' })

              if (attempt < RECAPTCHA_CONFIG.SCRIPT_RETRY_MAX) {
                const delays = RECAPTCHA_CONFIG.SCRIPT_RETRY_DELAYS
                const index = attempt - 1
                const delay = index >= 0 && index < delays.length ? delays[index] : delays[delays.length - 1]

                setTimeout(() => loadScript(attempt + 1), delay)
              } else {
                setLoadingError('reCAPTCHA initialization failed after retries')
              }
            }
          }, RECAPTCHA_CONFIG.INITIALIZATION_TIMEOUT)
        } else {
          setLoadingError('reCAPTCHA script loaded but grecaptcha not available')
        }
      }

      script.onerror = () => {
        const errorMsg = `Failed to load reCAPTCHA script (attempt ${attempt})`

        logError(new Error(errorMsg), { attempt, name: 'useReCaptcha_script_load_failed' })

        if (attempt < RECAPTCHA_CONFIG.SCRIPT_RETRY_MAX) {
          const delays = RECAPTCHA_CONFIG.SCRIPT_RETRY_DELAYS
          const index = attempt - 1
          const delay = index >= 0 && index < delays.length ? delays[index] : delays[delays.length - 1]

          setTimeout(() => loadScript(attempt + 1), delay)
        } else {
          setScriptLoaded(false)
          setClientReady(false)
          setLoadingError('Failed to load reCAPTCHA script after retries')
        }
      }

      document.body.appendChild(script)
    }

    loadScript(1)

    return () => {
      setScriptLoaded(false)
      setClientReady(false)
      setLoadingError(null)

      try {
        const scriptElement = document.getElementById(RECAPTCHA_CONFIG.SCRIPT_ID)
        const badge = document.querySelector(RECAPTCHA_CONFIG.BADGE_SELECTOR)

        if (scriptElement) {
          scriptElement.remove()
        }

        if (badge?.parentElement) {
          badge.parentElement.remove()
        }
      } catch {
        // Ignore cleanup errors
      }
    }
  }, [])

  const getReCaptcha = useCallback(() => {
    if (typeof window === 'undefined') {
      throw new Error('reCAPTCHA is not available in server-side environment')
    }

    if (!window.grecaptcha) {
      throw new Error('reCAPTCHA script not loaded')
    }

    return reCaptchaSchema.parse(window.grecaptcha)
  }, [])

  const executeRecaptcha = useCallback(
    async ({ action, reCaptchaKey }: ExecuteRecaptchaParams): Promise<string> => {
      const recaptcha = getReCaptcha()

      return new Promise<string>((resolve, reject) => {
        recaptcha.ready(() => {
          const timeout = setTimeout(() => {
            reject(new Error('recaptcha.execute stuck'))
          }, 5000)

          recaptcha
            .execute(reCaptchaKey, { action })
            .then(resolve)
            .catch(reject)
            .finally(() => {
              clearTimeout(timeout)
            })
        })
      })
    },
    [getReCaptcha],
  )

  const getRecaptchaAPIToken = useCallback(
    async (action: RecaptchaActions): Promise<string> => {
      if (!RECAPTCHA_KEY) {
        throw new Error('reCAPTCHA key is not configured')
      }

      return executeRecaptcha({ action, reCaptchaKey: RECAPTCHA_KEY })
    },
    [executeRecaptcha],
  )

  const verify = useCallback(
    async (action: RecaptchaActions): Promise<Record<string, string>> => {
      let attempt = 1

      while (attempt <= RECAPTCHA_CONFIG.MAX_RETRIES) {
        try {
          const token = await getRecaptchaAPIToken(action)

          return {
            [RECAPTCHA_HEADER]: token,
          }
        } catch (error) {
          logError(error, {
            attempt,
            name: 'useReCaptcha_verify_failed',
          })
          attempt++
          if (attempt > RECAPTCHA_CONFIG.MAX_RETRIES) {
            return {
              [RECAPTCHA_HEADER]: RECAPTCHA_CONFIG.ERROR_TOKEN,
            }
          }
        }
      }

      return {
        [RECAPTCHA_HEADER]: RECAPTCHA_CONFIG.ERROR_TOKEN,
      }
    },
    [getRecaptchaAPIToken],
  )

  return useMemo(
    () => ({
      verify,
      ready: scriptLoaded && clientReady && !loadingError,
    }),
    [verify, scriptLoaded, clientReady, loadingError],
  )
}
